import React, { useState, useRef } from 'react';
import {
    CompanySettingModel,
    PolicySettingModel,
    CompanyName,
    CompanySlogan,
    CompanyOwner,
    CompanyBusinessNumber,
    CompanyAddress,
    CompanyPhone,
    CompanyNaverBlogUrl,
    CompanyInstagramUrl,
    CompanyYoutubeUrl,
    SUBJECT_TYPES,
    SubjectType
} from '../../../models';
import { db, storage } from '../../../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ref as sRef, getDownloadURL } from 'firebase/storage';
import { uploadOptimizedImage } from '../../../services/imageOptimizer';
import { MapPin, Phone, X, FileText, Instagram, Youtube, UserPlus, GraduationCap, Send, CheckCircle2, Loader2, Camera, ImageIcon, Trash2 } from 'lucide-react';

interface LandingFooterProps {
    companyInfo: CompanySettingModel | null;
    policySettings?: PolicySettingModel | null;
    onNavigate?: (view: string) => void;
    onStart?: () => void;
}

const LandingFooter: React.FC<LandingFooterProps> = ({ companyInfo, policySettings, onNavigate, onStart }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [activePolicy, setActivePolicy] = useState<{ title: string; content: string } | null>(null);

    // Teacher Application Modal State
    const [applyModalOpen, setApplyModalOpen] = useState(false);
    const [applicantName, setApplicantName] = useState('');
    const [applicantPhone, setApplicantPhone] = useState('');
    const [applicantEmail, setApplicantEmail] = useState('');
    const [applicantSubject, setApplicantSubject] = useState<SubjectType>('국어');
    const [applicantCareer, setApplicantCareer] = useState('');
    const [applicantIntro, setApplicantIntro] = useState('');
    const [applicantPortfolioUrl, setApplicantPortfolioUrl] = useState('');
    const [applicantPhotoFile, setApplicantPhotoFile] = useState<File | null>(null);
    const [applicantPhotoPreview, setApplicantPhotoPreview] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submittedSuccess, setSubmittedSuccess] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('이미지 파일(JPG, PNG, WebP 등)만 업로드 가능합니다.');
            return;
        }

        setApplicantPhotoFile(file);
        setApplicantPhotoPreview(URL.createObjectURL(file));
    };

    const handleRemovePhoto = () => {
        setApplicantPhotoFile(null);
        setApplicantPhotoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const openPolicy = (type: 'tos' | 'privacy' | 'refund') => {
        if (!policySettings) return;
        let title = '';
        let content = '';

        switch (type) {
            case 'tos':
                title = '서비스 이용약관';
                content = policySettings.tos;
                break;
            case 'privacy':
                title = '개인정보 처리방침';
                content = policySettings.privacy;
                break;
            case 'refund':
                title = '취소 및 환불 정책';
                content = policySettings.refund;
                break;
        }

        setActivePolicy({ title, content });
        setModalOpen(true);
    };

    return (
        <footer className="bg-[#070D1E] border-t border-indigo-950/20 pt-12 pb-10 px-6 md:px-8 text-white relative overflow-hidden">
            <div className="max-w-6xl mx-auto relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 pb-12 md:pb-20 border-b border-indigo-950/30">
                    <div className="space-y-6 md:space-y-8">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                                <img src={companyInfo?.logoUrl || '/logo.png'} alt="Logo" className="w-full h-full object-contain p-0.5" />
                            </div>
                            <span className="text-xl md:text-2xl font-black tracking-tight">{companyInfo?.companyName || CompanyName}</span>
                        </div>
                        <p className="text-slate-400 font-medium leading-relaxed max-w-sm text-xs sm:text-sm whitespace-pre-wrap">
                            {companyInfo?.slogan || CompanySlogan}
                        </p>
                        <div className="flex items-center gap-3 pt-1 flex-wrap">
                            <a
                                href={CompanyNaverBlogUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-[11px] font-black border border-emerald-500/20 transition-all"
                            >
                                <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-black text-[8px] select-none">N</span>
                                네이버 블로그
                            </a>
                            <a
                                href={CompanyInstagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-pink-500/10 hover:bg-pink-500/20 text-pink-400 text-[11px] font-black border border-pink-500/20 transition-all"
                            >
                                <Instagram size={12} />
                                인스타그램
                            </a>
                            <a
                                href={CompanyYoutubeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[11px] font-black border border-red-500/20 transition-all"
                            >
                                <Youtube size={12} />
                                유튜브
                            </a>
                        </div>
                    </div>

                    <div className="flex flex-col gap-6 md:gap-8">
                        <div className="space-y-3">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-500">지점 정보</h4>
                            <ul className="space-y-2.5 text-xs font-bold text-slate-300">
                                <li className="flex items-start gap-2">
                                    <MapPin size={14} className="text-slate-600 shrink-0 mt-0.5" /> 
                                    <span>{companyInfo?.address || CompanyAddress}</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <Phone size={14} className="text-slate-600 shrink-0" /> 
                                    <span>{companyInfo?.phone || CompanyPhone}</span>
                                </li>
                            </ul>
                        </div>
                        <div className="space-y-3">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-500">회사 상세</h4>
                            <ul className="space-y-2.5 text-xs font-bold text-slate-300">
                                <li>대표: {companyInfo?.representativeName || CompanyOwner}</li>
                                <li>사업자번호: {companyInfo?.businessNumber || CompanyBusinessNumber}</li>
                                {(companyInfo?.mailOrderNumber || '') && <li>통신판매: {companyInfo?.mailOrderNumber}</li>}
                            </ul>
                        </div>
                        <div className="space-y-2.5 pt-2 border-t border-indigo-950/40">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-400 flex items-center gap-1.5">
                                <GraduationCap size={14} className="text-indigo-400" />
                                인재 채용 안내
                            </h4>
                            <p className="text-xs text-slate-400 font-medium leading-relaxed">
                                정성우학원과 함께 학생들의 성장을 만들어갈 우수한 대표 강사진을 상시 모집합니다.
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    setSubmittedSuccess(false);
                                    setApplyModalOpen(true);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600/20 hover:bg-indigo-600/35 text-indigo-300 hover:text-white text-xs font-black border border-indigo-500/30 transition-all cursor-pointer active:scale-95"
                            >
                                <UserPlus size={14} />
                                강사 지원서 작성하기
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row items-center justify-between pt-8 text-[10px] font-black text-slate-500 uppercase tracking-widest gap-4">
                    <p
                        onClick={onStart}
                        className="text-center md:text-left cursor-pointer hover:text-slate-300 transition-colors select-none"
                    >
                        © 2026 {companyInfo?.companyName || CompanyName}. ALL RIGHTS RESERVED.
                    </p>
                </div>
            </div>

            {/* Background Accent */}
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-indigo-600/10 blur-[100px] rounded-full"></div>

            {/* Policy Modal */}
            {modalOpen && activePolicy && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm animate-in fade-in" onClick={() => setModalOpen(false)}></div>
                    <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[80vh] animate-in zoom-in-95">
                        <div className="flex items-center justify-between p-6 border-b border-slate-100">
                            <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                                <FileText className="text-indigo-600" size={20} />
                                {activePolicy.title}
                            </h3>
                            <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <div className="prose prose-sm max-w-none text-slate-600 whitespace-pre-line leading-relaxed">
                                {activePolicy.content || '내용이 등록되지 않았습니다.'}
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setModalOpen(false)}
                                className="bg-slate-800 text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-900 transition-colors"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Teacher Application Modal */}
            {applyModalOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300 cursor-pointer"
                        onClick={() => setApplyModalOpen(false)}
                    ></div>
                    <div className="relative w-full max-w-2xl bg-[#0F172A] border border-indigo-950/60 rounded-[2.5rem] shadow-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300 text-left text-white space-y-6 select-text">
                        {/* Modal Header */}
                        <div className="flex items-start justify-between gap-4 border-b border-indigo-950/50 pb-5">
                            <div className="space-y-1">
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-400 font-black text-[10px] uppercase tracking-wider border border-indigo-500/20">
                                    <GraduationCap size={13} />
                                    Teacher Recruitment
                                </div>
                                <h3 className="text-xl sm:text-2xl font-black text-white">
                                    정성우학원 강사 채용 지원
                                </h3>
                                <p className="text-xs sm:text-sm text-slate-400 font-medium">
                                    학생들의 성장을 함께 만들어갈 열정적이고 역량 있는 강사진을 모십니다.
                                </p>
                            </div>
                            <button 
                                onClick={() => setApplyModalOpen(false)}
                                className="text-slate-400 hover:text-white transition-colors p-2 rounded-xl bg-slate-900/60 border border-indigo-950/40 cursor-pointer shrink-0"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {submittedSuccess ? (
                            <div className="py-12 flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in duration-300">
                                <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center border border-emerald-500/30">
                                    <CheckCircle2 size={36} />
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-xl font-black text-white">지원서가 성공적으로 제출되었습니다!</h4>
                                    <p className="text-sm text-slate-400 max-w-md mx-auto leading-relaxed">
                                        작성해 주신 지원서는 최고관리자가 검토 후 연락드릴 예정입니다. 정성우학원에 관심 가져주셔서 감사드립니다.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setApplyModalOpen(false)}
                                    className="mt-4 px-8 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-black transition-all shadow-lg cursor-pointer"
                                >
                                    확인
                                </button>
                            </div>
                        ) : (
                            <form 
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    if (!applicantName.trim() || !applicantPhone.trim() || !applicantCareer.trim() || !applicantIntro.trim()) {
                                        alert('성함, 연락처, 주요 경력, 자기소개 필수 항목을 입력해 주세요.');
                                        return;
                                    }
                                    setSubmitting(true);
                                    try {
                                        let photoUrl = '';
                                        if (applicantPhotoFile) {
                                            const fileName = `${Date.now()}_${applicantPhotoFile.name.replace(/\.[^/.]+$/, "")}.webp`;
                                            const storageRef = sRef(storage, `teacherApplications/${fileName}`);
                                            const snapshot = await uploadOptimizedImage(storageRef, applicantPhotoFile, { maxWidth: 1000, quality: 0.85 });
                                            photoUrl = await getDownloadURL(snapshot.ref);
                                        }

                                        await addDoc(collection(db, 'teacherApplications'), {
                                            name: applicantName.trim(),
                                            phone: applicantPhone.trim(),
                                            email: applicantEmail.trim(),
                                            imageUrl: photoUrl || '',
                                            subject: applicantSubject,
                                            career: applicantCareer.trim(),
                                            intro: applicantIntro.trim(),
                                            portfolioUrl: applicantPortfolioUrl.trim(),
                                            status: 'pending',
                                            createdAt: Date.now()
                                        });
                                        setSubmittedSuccess(true);
                                        // Reset fields
                                        setApplicantName('');
                                        setApplicantPhone('');
                                        setApplicantEmail('');
                                        setApplicantCareer('');
                                        setApplicantIntro('');
                                        setApplicantPortfolioUrl('');
                                        setApplicantPhotoFile(null);
                                        setApplicantPhotoPreview(null);
                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                    } catch (err) {
                                        console.error('Error submitting application:', err);
                                        alert('지원서 작성 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
                                    } finally {
                                        setSubmitting(false);
                                    }
                                }}
                                className="space-y-5"
                            >
                                {/* Photo Upload Section */}
                                <div className="space-y-1.5 text-left">
                                    <label className="text-xs font-black text-slate-300 flex items-center justify-between">
                                        <span className="flex items-center gap-1.5">
                                            <Camera size={14} className="text-indigo-400" />
                                            지원자 사진 (선택)
                                        </span>
                                        <span className="text-[11px] font-normal text-slate-400">증명사진 / 프로필 사진 (JPG, PNG, WebP)</span>
                                    </label>
                                    
                                    <div className="flex items-center gap-4 p-3.5 bg-slate-900/80 border border-indigo-950/60 rounded-2xl">
                                        <div className="relative w-20 h-24 rounded-xl overflow-hidden bg-slate-950 border border-indigo-950/50 flex items-center justify-center shrink-0 shadow-inner group">
                                            {applicantPhotoPreview ? (
                                                <>
                                                    <img 
                                                        src={applicantPhotoPreview} 
                                                        alt="Applicant Preview" 
                                                        className="w-full h-full object-cover" 
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={handleRemovePhoto}
                                                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-rose-400 cursor-pointer"
                                                        title="사진 삭제"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center text-slate-600">
                                                    <ImageIcon size={22} />
                                                    <span className="text-[9px] mt-1 font-bold">사진 없음</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 flex flex-col justify-center gap-2">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handlePhotoSelect}
                                                accept="image/*"
                                                className="hidden"
                                            />
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="px-4 py-2 rounded-xl bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
                                                >
                                                    <Camera size={13} />
                                                    {applicantPhotoFile ? '사진 변경하기' : '사진 첨부하기'}
                                                </button>
                                                {applicantPhotoFile && (
                                                    <button
                                                        type="button"
                                                        onClick={handleRemovePhoto}
                                                        className="px-3 py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 text-xs font-bold transition-all flex items-center gap-1 cursor-pointer"
                                                    >
                                                        <Trash2 size={13} />
                                                        삭제
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-slate-500">
                                                선택 사항이며, 첨부 시 관리자 검토 화면에 프로필 사진으로 함께 전달됩니다.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5 text-left">
                                        <label className="text-xs font-black text-slate-300">
                                            지원자 성함 <span className="text-indigo-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={applicantName}
                                            onChange={(e) => setApplicantName(e.target.value)}
                                            placeholder="예: 홍길동"
                                            className="w-full bg-slate-900/80 border border-indigo-950/60 rounded-xl px-4 py-2.5 text-sm font-medium text-white outline-none focus:border-indigo-500 transition-all placeholder:text-slate-600"
                                        />
                                    </div>
                                    <div className="space-y-1.5 text-left">
                                        <label className="text-xs font-black text-slate-300">
                                            연락처 <span className="text-indigo-400">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            required
                                            value={applicantPhone}
                                            onChange={(e) => setApplicantPhone(e.target.value)}
                                            placeholder="예: 010-1234-5678"
                                            className="w-full bg-slate-900/80 border border-indigo-950/60 rounded-xl px-4 py-2.5 text-sm font-medium text-white outline-none focus:border-indigo-500 transition-all placeholder:text-slate-600"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5 text-left">
                                        <label className="text-xs font-black text-slate-300">
                                            이메일 주소
                                        </label>
                                        <input
                                            type="email"
                                            value={applicantEmail}
                                            onChange={(e) => setApplicantEmail(e.target.value)}
                                            placeholder="teacher@example.com"
                                            className="w-full bg-slate-900/80 border border-indigo-950/60 rounded-xl px-4 py-2.5 text-sm font-medium text-white outline-none focus:border-indigo-500 transition-all placeholder:text-slate-600"
                                        />
                                    </div>
                                    <div className="space-y-1.5 text-left">
                                        <label className="text-xs font-black text-slate-300">
                                            담당 과목 <span className="text-indigo-400">*</span>
                                        </label>
                                        <select
                                            value={applicantSubject}
                                            onChange={(e) => setApplicantSubject(e.target.value as SubjectType)}
                                            className="w-full bg-slate-900/80 border border-indigo-950/60 rounded-xl px-4 py-2.5 text-sm font-medium text-white outline-none focus:border-indigo-500 transition-all cursor-pointer"
                                        >
                                            {SUBJECT_TYPES.map((sub) => (
                                                <option key={sub} value={sub} className="bg-slate-900 text-white">{sub}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-1.5 text-left">
                                    <label className="text-xs font-black text-slate-300">
                                        주요 학력 및 약력 / 출강 경력 <span className="text-indigo-400">*</span>
                                    </label>
                                    <textarea
                                        required
                                        rows={3}
                                        value={applicantCareer}
                                        onChange={(e) => setApplicantCareer(e.target.value)}
                                        placeholder="출신 대학/전공, 학원 출강 및 대표 경력사항을 항목별로 적어주세요."
                                        className="w-full bg-slate-900/80 border border-indigo-950/60 rounded-xl p-3.5 text-sm font-medium text-white outline-none focus:border-indigo-500 transition-all placeholder:text-slate-600 resize-none"
                                    />
                                </div>

                                <div className="space-y-1.5 text-left">
                                    <label className="text-xs font-black text-slate-300">
                                        자기소개 및 지도 철학 <span className="text-indigo-400">*</span>
                                    </label>
                                    <textarea
                                        required
                                        rows={3}
                                        value={applicantIntro}
                                        onChange={(e) => setApplicantIntro(e.target.value)}
                                        placeholder="수업 특징, 학생 관리 방식, 지도 모토 등을 작성해 주세요."
                                        className="w-full bg-slate-900/80 border border-indigo-950/60 rounded-xl p-3.5 text-sm font-medium text-white outline-none focus:border-indigo-500 transition-all placeholder:text-slate-600 resize-none"
                                    />
                                </div>

                                <div className="space-y-1.5 text-left">
                                    <label className="text-xs font-black text-slate-300">
                                        포트폴리오 / 이력서 링크 (선택)
                                    </label>
                                    <input
                                        type="url"
                                        value={applicantPortfolioUrl}
                                        onChange={(e) => setApplicantPortfolioUrl(e.target.value)}
                                        placeholder="https://drive.google.com/... 또는 웹이력서 URL"
                                        className="w-full bg-slate-900/80 border border-indigo-950/60 rounded-xl px-4 py-2.5 text-sm font-medium text-white outline-none focus:border-indigo-500 transition-all placeholder:text-slate-600"
                                    />
                                </div>

                                <div className="pt-3 flex justify-end gap-3 border-t border-indigo-950/50">
                                    <button
                                        type="button"
                                        onClick={() => setApplyModalOpen(false)}
                                        className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-400 hover:text-white transition-colors cursor-pointer"
                                        disabled={submitting}
                                    >
                                        취소
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black transition-all flex items-center gap-2 shadow-lg shadow-indigo-900/30 cursor-pointer disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 size={14} className="animate-spin" />
                                                제출 중...
                                            </>
                                        ) : (
                                            <>
                                                <Send size={14} />
                                                강사 지원서 제출하기
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </footer>
    );
};

export default LandingFooter;
